/**
 * Image processing — compression, thumbnail generation,
 * responsive image sizes, avatar cropping helpers, and
 * WebP conversion using the browser Canvas API.
 *
 * No external dependencies — uses native Canvas and
 * createImageBitmap APIs.
 */

import type { ResponsiveImageSet } from './types';

const RESPONSIVE_WIDTHS = { small: 320, medium: 640, large: 1280 };
const THUMBNAIL_WIDTH = 160;
const AVATAR_SIZE = 256;
const DEFAULT_QUALITY = 0.85;

export interface ProcessedImage {
  blob: Blob;
  width: number;
  height: number;
}

export async function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image.'));
    };
    img.src = url;
  });
}

export async function getImageDimensions(
  file: Blob,
): Promise<{ width: number; height: number }> {
  const img = await loadImage(file);
  return { width: img.naturalWidth, height: img.naturalHeight };
}

export async function compressImage(
  file: Blob,
  maxDimension?: number,
  quality: number = DEFAULT_QUALITY,
  format: 'image/jpeg' | 'image/webp' | 'image/png' = 'image/jpeg',
): Promise<ProcessedImage> {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('Canvas 2D context unavailable.');

  let { width, height } = { width: img.naturalWidth, height: img.naturalHeight };

  if (maxDimension) {
    const ratio = Math.min(maxDimension / width, maxDimension / height, 1);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  canvas.width = width;
  canvas.height = height;

  if (format === 'image/jpeg' || format === 'image/webp') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }

  ctx.drawImage(img, 0, 0, width, height);

  const blob = await canvasToBlob(canvas, format, quality);
  return { blob, width, height };
}

export async function generateThumbnail(
  file: Blob,
  width: number = THUMBNAIL_WIDTH,
  format: 'image/jpeg' | 'image/webp' = 'image/jpeg',
  quality: number = 0.8,
): Promise<ProcessedImage> {
  const img = await loadImage(file);
  const ratio = width / img.naturalWidth;
  const height = Math.round(img.naturalHeight * ratio);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');

  canvas.width = width;
  canvas.height = height;

  if (format === 'image/jpeg' || format === 'image/webp') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }

  ctx.drawImage(img, 0, 0, width, height);

  const blob = await canvasToBlob(canvas, format, quality);
  return { blob, width, height };
}

export async function generateResponsiveImages(
  file: Blob,
  format: 'image/jpeg' | 'image/webp' = 'image/jpeg',
  quality: number = DEFAULT_QUALITY,
): Promise<ResponsiveImageSet & { blobs: { small: Blob; medium: Blob; large: Blob } }> {
  const [small, medium, large] = await Promise.all([
    compressImage(file, RESPONSIVE_WIDTHS.small, quality, format),
    compressImage(file, RESPONSIVE_WIDTHS.medium, quality, format),
    compressImage(file, RESPONSIVE_WIDTHS.large, quality, format),
  ]);

  return {
    small: `responsive-small-${Date.now()}`,
    medium: `responsive-medium-${Date.now()}`,
    large: `responsive-large-${Date.now()}`,
    blobs: { small: small.blob, medium: medium.blob, large: large.blob },
  };
}

export async function cropAvatar(
  file: Blob,
  size: number = AVATAR_SIZE,
  format: 'image/jpeg' | 'image/webp' = 'image/jpeg',
  quality: number = 0.9,
): Promise<ProcessedImage> {
  const img = await loadImage(file);
  const sourceSize = Math.min(img.naturalWidth, img.naturalHeight);
  const sourceX = (img.naturalWidth - sourceSize) / 2;
  const sourceY = (img.naturalHeight - sourceSize) / 2;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');

  canvas.width = size;
  canvas.height = size;

  if (format === 'image/jpeg' || format === 'image/webp') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
  }

  ctx.drawImage(img, sourceX, sourceY, sourceSize, sourceSize, 0, 0, size, size);

  const blob = await canvasToBlob(canvas, format, quality);
  return { blob, width: size, height: size };
}

export function supportsWebP(): boolean {
  if (typeof document === 'undefined') return false;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  format: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob returned null.'));
      },
      format,
      quality,
    );
  });
}

export { RESPONSIVE_WIDTHS, THUMBNAIL_WIDTH, AVATAR_SIZE };
